import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import moment, { Moment } from 'moment';

/*
 * Formats are from the fhir docs: https://www.hl7.org/fhir/datatypes.html#time
 * Current implemenation ignores milliseconds
 */
const fhirFormats = {
  time: 'HH:mm:ss',
  dateTime: 'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
  date: 'YYYY-MM-DD',
};

export const getDefaultFhirValues = () => ({
  time: moment().format(fhirFormats.time),
  dateTime: moment().format(fhirFormats.dateTime),
  date: moment().format(fhirFormats.date),
});

export const inputFormats = {
  time: 'HH:mm',
  date: 'MM/DD/YYYY',
  dateTime: 'MM/DD/YYYY HH:mm',
};

export interface Parser {
  isValid: (fhirValue: string) => boolean;
  toInputValue: (fhirValue: string) => Moment;
  fromInputValue: (inputValue: Moment) => string;
}

const validityCheck = (
  fhirValue: string,
  format: string,
  isStrict: boolean,
  nullValid = false,
) => {
  try {
    if (isNil(fhirValue) && nullValid) return true;

    if (isNil(fhirValue) || isEmpty(fhirValue) || !isString(fhirValue)) {
      return false;
    }

    return moment(fhirValue, format, isStrict).isValid();
  } catch (e) {
    console.error(e);
    return false;
  }
};

const getParser = (
  type: 'time' | 'date' | 'dateTime',
  strictFhirFormatCheck = false,
): Parser => {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  let fhirFormat: string = null;

  switch (type) {
    case 'time':
      fhirFormat = fhirFormats.time;
      break;
    case 'date':
      fhirFormat = fhirFormats.date;
      break;
    case 'dateTime':
      fhirFormat = fhirFormats.dateTime;
      break;
    default:
      console.error(
        `getTimeParser received unknown fhir type of: "${type}".  Defaulting to dateTime type.`,
      );
      fhirFormat = fhirFormats.dateTime;
  }

  return {
    isValid: (fhirValue: string, nullValid = false) => {
      return validityCheck(
        fhirValue,
        fhirFormat,
        strictFhirFormatCheck,
        nullValid,
      );
    },
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    toInputValue: (fhirValue: string) => {
      if (fhirValue && moment(fhirValue, fhirFormat, true).isValid()) {
        return moment(fhirValue, fhirFormat);
      } else {
        return null;
      }
    },
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    fromInputValue: (inputValue: Moment) => {
      if (!inputValue || !moment.isMoment(inputValue)) return null;

      return inputValue.isValid()
        ? inputValue
            .seconds(0)
            .milliseconds(0) // floor seconds and milliseconds
            .format(fhirFormat)
        : null;
    },
  };
};

export const timeParser = getParser('time', true); // schema requires hours, minutes, and seconds
export const dateParser = getParser('date');
export const dateTimeParser = getParser('dateTime');

export const dateFormatValidation = (
  value: any,
  type: any,
  formatMessage: (message: any) => string,
) => {
  const isDateTimeType =
    type === 'date' || type === 'time' || type === 'dateTime';
  if (!isDateTimeType || isNil(value)) return;

  let parser = timeParser;

  if (type === 'date') {
    parser = dateParser;
  } else if (type === 'dateTime') {
    parser = dateTimeParser;
  }

  if (!parser.isValid(value)) {
    return formatMessage({
      id: 'surveys.validation.dateFormat.invalid',
      defaultMessage: 'Invalid Format',
    });
  }
};
